import { useRef, useSyncExternalStore, type ReactElement } from "react";
import { HUD } from "odyssey-shared";
import { Button, PersistentNotice, logical, useCentred } from "../../ui/kit/index.ts";
import { keepFocusBeside, useModal } from "../../ui/kit/modal.ts";
import type { ModerationNotices, ShownNotice } from "./notices.ts";

/**
 * What a player a GM acted on is shown over the game (ADR-056's amendment of 2026-10-04, items 2 and 9; the
 * design's `ModerationNotice`, a visual reference): the kit's persistent notice, led by the alert icon.
 *
 * A warning stands in the middle of a scrim over the game and is a modal until its button is pressed: it is the
 * input router's topmost layer and holds the keyboard (`useModal`, as the kit's dialogs do), so no key reaches the
 * game behind it, Tab stays on its button, and Escape is taken and does nothing, since a warning is dismissed by
 * its button alone.
 *
 * A freeze has no button and is no modal: it stands at the top centre of the view with no scrim, and neither it
 * nor its layer takes the pointer or the keyboard, because a frozen player can still talk and must reach the chat.
 *
 * The reason in either sentence is a GM's own words, drawn only as a text child. The component imports no
 * stylesheet, so it renders to static markup under `bun test`; `mount.tsx` imports the sheets.
 */

/**
 * How the notices lay out, in logical pixels: a notice's width, and a freeze's distance from the view's top edge,
 * the HUD's margin.
 */
export const MODERATION_NOTICE = { width: 220, top: HUD.margin } as const;

/**
 * Does nothing: what Escape asks of a warning, which stays until its button is pressed.
 */
function stay(): void {
  return undefined;
}

/**
 * A warning: the scrim, and in its middle on whole logical pixels the notice with its button, holding the
 * keyboard while it shows.
 *
 * @param props The `notice` and `onDismiss`, run when its button is pressed.
 * @returns The scrim holding the notice.
 * @throws Error when mounted outside a `KitInputProvider`.
 */
function WarnNotice({ notice, onDismiss }: { readonly notice: ShownNotice; readonly onDismiss: () => void }): ReactElement {
  const scrim = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const place = useCentred(scrim, box, { x: true, y: true }, notice.sentence);
  const { trap } = useModal(box, () => ({ content: box.current === null ? [] : [box.current], close: null }), stay);
  return (
    <div ref={scrim} className="ot-scrim ot-moderation-notice" data-moderation="warn" onMouseDown={keepFocusBeside}>
      <div ref={box} tabIndex={-1} className="ot-moderation-notice__box" onKeyDown={trap} style={{ left: logical(place.left), top: logical(place.top) }}>
        <PersistentNotice
          icon="alert"
          title={notice.title}
          sentence={notice.sentence}
          width={MODERATION_NOTICE.width}
          actions={
            <Button variant="primary" onClick={onDismiss}>
              {notice.action}
            </Button>
          }
        />
      </div>
    </div>
  );
}

/**
 * A freeze: the notice with no button at the top centre of the view, on whole logical pixels, over no scrim and
 * taking no pointer events.
 *
 * @param props The `notice`.
 * @returns The layer holding the notice.
 */
function FreezeNotice({ notice }: { readonly notice: ShownNotice }): ReactElement {
  const area = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const place = useCentred(area, box, { x: true, y: false }, notice.sentence);
  return (
    <div ref={area} className="ot-moderation-notice ot-moderation-notice--freeze" data-moderation="freeze">
      <div ref={box} className="ot-moderation-notice__box" style={{ left: logical(place.left), top: logical(MODERATION_NOTICE.top) }}>
        <PersistentNotice icon="alert" title={notice.title} sentence={notice.sentence} width={MODERATION_NOTICE.width} />
      </div>
    </div>
  );
}

/**
 * The notice the page's moderation shows now, following its store: a warning, a freeze, or nothing.
 *
 * @param props The page's `notices`.
 * @returns The notice's layer, or null while there is none.
 * @throws Error when a warning shows outside a `KitInputProvider`.
 * @example
 * <ModerationNoticeLayer notices={notices} />
 */
export function ModerationNoticeLayer({ notices }: { readonly notices: ModerationNotices }): ReactElement | null {
  const { notice } = useSyncExternalStore(notices.subscribe, notices.getSnapshot, notices.getSnapshot);
  if (notice === null) {
    return null;
  }
  return notice.kind === "warn" ? <WarnNotice notice={notice} onDismiss={notices.dismiss} /> : <FreezeNotice notice={notice} />;
}
